import type { ComponentType, ReactNode } from 'react';
import type { RouteObject } from 'react-router';
import type { Idioma } from '../i18n';

/**
 * Contrato do lado web de um módulo (docs/CONTRATOS.md §4). Cada módulo exporta `modulo` em
 * apps/web/src/modules/<slug>/index.tsx; o núcleo registra sozinho (import.meta.glob), monta a página de
 * seção do menu com as abas de todos os módulos instalados daquele menu e as rotas extras (fichas).
 */
export interface AbaModulo {
  /** chave i18n do rótulo no namespace do módulo, ex.: 'abas.clientes' */
  rotulo: string;
  /** caminho relativo ao menu, ex.: 'clientes' vira /cadastros/clientes */
  caminho: string;
  elemento: ReactNode;
  /** permissão para ver a aba (padrão '<slug>.ler') */
  permissao?: string;
  /** botão primário do cabeçalho quando esta aba está ativa (F6) */
  Acao?: ComponentType;
  ordem?: number;
}

export interface ModuloWeb {
  slug: string;
  /** chave do menu principal (F5): cadastros, comercial, financeiro, agenda, pessoas, operacao, treinamento, integracoes, admin */
  menu: string;
  abas: AbaModulo[];
  /** rotas fora das abas, ex.: ficha /cadastros/parceiros/:id */
  rotas?: RouteObject[];
  dicionario: Record<Idioma, Record<string, unknown>>;
  /** indicadores para o Início (F10) */
  Indicadores?: ComponentType;
  /**
   * M1.7: painéis que o módulo mostra na ficha de uma entidade de outro módulo, ex.: Pipeline mostra as
   * oportunidades na ficha do parceiro (entidade 'cadastros.parceiro'). O painel busca na API do próprio módulo.
   */
  paineis?: PainelEntidade[];
  /** F9 e F10: ação mais comum do módulo, oferecida como botão primário do Início (rótulo é chave i18n do módulo). */
  acaoInicio?: { rotulo: string; rota: string; permissao: string; ordem?: number };
}

export interface PainelEntidade {
  entidade: string;
  /** chave i18n do título no namespace do módulo */
  titulo: string;
  /** permissão para ver o painel (padrão '<slug>.ler') */
  permissao?: string;
  Componente: ComponentType<{ id: string }>;
  ordem?: number;
}

// Eager somente para o contrato: os índices mantêm metadados e indicadores síncronos,
// mas exportam telas, ações e painéis por telaSobDemanda (sem iniciar o download aqui).
const encontrados = import.meta.glob<{ modulo: ModuloWeb }>('../modules/*/index.tsx', { eager: true });

export const MODULOS_WEB: ModuloWeb[] = Object.values(encontrados)
  .map((m) => m.modulo)
  .filter(Boolean);

/** M1.7: painéis de outros módulos para a ficha de uma entidade, só dos módulos instalados que a sessão pode ler. */
export function paineisDaEntidade(entidade: string, instalados: readonly string[], permissoes: readonly string[]): (PainelEntidade & { slug: string })[] {
  const pode = (p: string) => permissoes.includes('*') || permissoes.includes(p) || permissoes.includes(`${p.split('.')[0]}.*`);
  return MODULOS_WEB.filter((m) => instalados.includes(m.slug))
    .flatMap((m) => (m.paineis ?? []).filter((p) => p.entidade === entidade && pode(p.permissao ?? `${m.slug}.ler`)).map((p) => ({ ...p, slug: m.slug })))
    .sort((a, b) => (a.ordem ?? 50) - (b.ordem ?? 50));
}
